import * as React from 'react'

import { captureException } from '@sentry/nextjs'

import { IDialogContentButtonProps } from '~/components/component-library/Dialog'
import { isMacOS } from '~/utils/devices'
import { sendTrackingData } from '~/utils/tracking'

interface IDialogKeyboardProps {
  id: string
  isLoading?: boolean
  actionButton?: IDialogContentButtonProps | { element: React.ReactElement }
  deleteButton?: IDialogContentButtonProps
}

/**
 * Handle keyboard shortcuts (Cmd/Ctrl+Enter) for dialog actions
 */
export const useDialogKeyboard = ({
  id,
  isLoading,
  actionButton,
  deleteButton,
}: IDialogKeyboardProps) => {
  React.useEffect(() => {
    const handleKeyboardShortcuts = (event: KeyboardEvent) => {
      try {
        if (!isPrimaryActionShortcut(event) || isLoading) return

        // Only handle if dialog is focused
        const activeElement = document.activeElement
        const dialogElement = document.querySelector(`[data-dialog-id="${id}"]`)

        const activeDialog = activeElement?.closest('[data-dialog-id]')
        if (activeDialog) {
          // Focus is inside a dialog — only fire for that dialog
          if (activeDialog !== dialogElement) return
        } else {
          // Focus is outside every dialog (e.g. restored to body after a
          // nested modal closed) — only fire for the topmost open dialog
          const allDialogs = document.querySelectorAll('[data-dialog-id]')
          if (allDialogs[allDialogs.length - 1] !== dialogElement) return
        }

        const primaryButton = getPrimaryActionButton(actionButton, deleteButton)
        if (primaryButton?.onClick) {
          event.preventDefault()
          // Match ButtonInner: send the button's tracking event before its callback
          if (primaryButton.eventName)
            sendTrackingData(
              primaryButton.eventName,
              primaryButton.eventProperties,
            )
          primaryButton.onClick(
            event as unknown as React.MouseEvent<HTMLButtonElement>,
          )
        }
      } catch {
        // Silently fail
        captureException(`Error handling keyboard shortcuts for dialog ${id}`)
      }
    }

    document.addEventListener('keydown', handleKeyboardShortcuts)
    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcuts)
    }
  }, [id, isLoading, actionButton, deleteButton])
}

/** Checks if keyboard event is Cmd/Ctrl+Enter */
const isPrimaryActionShortcut = (event: KeyboardEvent): boolean => {
  const isModifierPressed = isMacOS() ? event.metaKey : event.ctrlKey
  return isModifierPressed && event.key === 'Enter'
}

/** Checks if button is disabled or has no onClick */
const isButtonDisabled = (button: IDialogContentButtonProps) => {
  return button.isDisabled || !button.onClick
}

/** Type guard for standard button with onClick */
const isStandardButton = (
  button: IDialogContentButtonProps | { element: React.ReactElement },
): button is IDialogContentButtonProps => {
  return 'onClick' in button
}

/**
 * Gets primary button by priority:
 * 1. Action button
 * 2. Delete button
 */
const getPrimaryActionButton = (
  actionButton?: IDialogContentButtonProps | { element: React.ReactElement },
  deleteButton?: IDialogContentButtonProps,
): IDialogContentButtonProps | null => {
  // Priority 1: Action button
  if (
    actionButton &&
    isStandardButton(actionButton) &&
    !isButtonDisabled(actionButton)
  ) {
    return actionButton
  }

  // Priority 2: Delete button
  if (deleteButton && !isButtonDisabled(deleteButton)) {
    return deleteButton
  }

  return null
}
